// Lifted from mobile LoggerScreen (ALIGNMENT_AUDIT HIGH 2).
// Web should adopt over time — today it surfaces save errors
// inconsistently because it has no equivalent of these helpers.
//
// The signatures take `unknown` so callers don't have to narrow before
// invoking. Each surface owns its own `ApiError` class (mobile in
// `apps/mobile/src/api/index.ts`, web in `apps/web/src/api.ts`); to
// stay surface-agnostic we duck-type on the shape both classes share:
// `{ status: number; body: unknown }` plus a string `message`. That's
// stable because both classes hand-roll the same `Error` extension.

type ApiErrorLike = {
  readonly status: number;
  readonly body: unknown;
  readonly message: string;
};

const isApiErrorLike = (err: unknown): err is ApiErrorLike => {
  if (!(err instanceof Error)) return false;
  const candidate = err as Error & { status?: unknown; body?: unknown };
  return typeof candidate.status === "number" && "body" in candidate;
};

/**
 * Tells a network-layer failure (no response) apart from a server-side
 * failure (HTTP non-2xx). An `ApiError` (duck-typed) means the server
 * replied; anything else — `TypeError` from `fetch`, abort errors,
 * timeout messages — is a transport problem and the caller should
 * enqueue/retry rather than display a server error slug.
 */
export const isNetworkError = (err: unknown): boolean => {
  if (isApiErrorLike(err)) return false;
  if (err instanceof TypeError) return true;
  const msg = err instanceof Error ? err.message : String(err);
  return /network|fetch|timeout|connection/i.test(msg);
};

/**
 * Surface the most informative slice of an `ApiError` body so the user
 * can act on it: the server's `error` slug for known problems
 * (`invalid_payload`, `invalid_idempotency_key`, `duplicate_id`,
 * `forbidden`, …), plus any first-issue path/message from a Zod
 * validation failure. Falls back to the HTTP status code, then to the
 * stringified error if the input isn't an `ApiError` at all.
 */
export const apiErrorSummary = (err: unknown): string => {
  if (!isApiErrorLike(err)) {
    if (err instanceof Error) return err.message;
    return String(err);
  }
  const body = err.body;
  if (body && typeof body === "object") {
    const errSlug = (body as { error?: unknown }).error;
    const issues = (body as { issues?: unknown }).issues;
    const issueLine =
      Array.isArray(issues) && issues.length > 0
        ? (() => {
            const first = issues[0] as {
              path?: unknown;
              message?: unknown;
            };
            const p = Array.isArray(first.path) ? first.path.join(".") : "";
            const m = typeof first.message === "string" ? first.message : "";
            return p || m ? `${p}${p && m ? ": " : ""}${m}` : "";
          })()
        : "";
    if (typeof errSlug === "string") {
      return issueLine ? `${errSlug} (${issueLine})` : errSlug;
    }
  }
  return `HTTP ${err.status}`;
};
